import { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { parse, format, isLastDayOfMonth, parseISO } from 'date-fns';
import PropTypes from 'prop-types';

import { PRIMARY_BASE_COLOR } from '../../core/constants';
import { getFormatString } from '../../core/utils';

const formatXAxis = (tickItem, isGroupedByMonth) => {
  const parsedDate = parse(tickItem, getFormatString(tickItem), new Date());
  if (isGroupedByMonth) {
    return format(parsedDate, `MMM ''yy`);
  }
  return format(parsedDate, 'MMM d');
};

const formatYAxis = (tickItem) => {
  return new Intl.NumberFormat('en', { notation: 'compact' }).format(tickItem);
};

const CustomTooltip = ({ active, payload, isGroupedByMonth }) => {
  if (!active || !payload || !payload.length) return null;

  const {
    payload: { date, totalDSSubscribedAndOptedInContacts, totalNonDSSubscribedAndOptedInContacts },
  } = payload[0];
  const parsedDate = parse(date, getFormatString(date), new Date());

  return (
    <div className="space-y-1 rounded-lg bg-white-100 px-3 py-2 shadow-custom">
      <p className="text-sm text-gray-800">{format(parsedDate, isGroupedByMonth ? `MMM yyyy` : 'MMM d, yyyy')}</p>
      <p className="text-lg text-primary-700" style={{ color: PRIMARY_BASE_COLOR }}>
        From DonorSpring: {totalDSSubscribedAndOptedInContacts}
      </p>
      <p className="text-lg text-primary-700" style={{ color: '#2F95E9' }}>
        Not From DonorSpring: {totalNonDSSubscribedAndOptedInContacts}
      </p>
    </div>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  isGroupedByMonth: PropTypes.bool,
};

const margin = {
  top: 12,
  right: 0,
  left: -6,
  bottom: 0,
};

const primaryDot = { stroke: '#FFFFFF', strokeWidth: 2, fill: PRIMARY_BASE_COLOR, r: 4 };
const primaryActiveDot = { stroke: '#FFFFFF', strokeWidth: 2, fill: PRIMARY_BASE_COLOR, r: 6 };
const secondaryDot = { stroke: '#FFFFFF', strokeWidth: 2, fill: '#2F95E9', r: 4 };
const secondaryActiveDot = { stroke: '#FFFFFF', strokeWidth: 2, fill: '#2F95E9', r: 6 };

const axisLine = { stroke: '#C9C9C9' };

const SubscribedContactsWidget = ({ data }) => {
  const isGroupedByMonth = useMemo(() => {
    return data && data.length > 0 && data.every((item) => isLastDayOfMonth(parseISO(item.date)));
  }, [data]);

  return (
    <div className="flex h-full flex-col space-y-5 rounded-xl bg-white-100 p-6">
      <div className="flex justify-between">
        <h3 className="text-lg font-semibold">Subscribed Contacts</h3>
        <div className="flex items-end gap-4">
          <div className="flex items-center space-x-1.5">
            <div className="size-2 rounded-full bg-primary-700" />
            <p className="text-sm">From DonorSpring</p>
          </div>

          <div className="flex items-center space-x-1.5">
            <div className="size-2 rounded-full bg-primary-500" />
            <p className="text-sm">Not From DonorSpring</p>
          </div>
        </div>
      </div>
      <div className="flex flex-1 items-center">
        <ResponsiveContainer width="100%" height={284}>
          <AreaChart width={696} height={284} data={data} margin={margin}>
            <defs>
              <linearGradient id="primaryGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={PRIMARY_BASE_COLOR} stopOpacity={0.15} />
                <stop offset="100%" stopColor={PRIMARY_BASE_COLOR} stopOpacity={0.015} />
              </linearGradient>
              <linearGradient id="secondaryGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2F95E9" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#2F95E9" stopOpacity={0.015} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(tickItem) => formatXAxis(tickItem, isGroupedByMonth)}
              tickLine={false}
              tickMargin={12}
              fontSize={12}
              axisLine={axisLine}
            />
            <YAxis tickLine={false} tickMargin={12} fontSize={12} axisLine={axisLine} tickFormatter={formatYAxis} />
            <Tooltip content={<CustomTooltip isGroupedByMonth={isGroupedByMonth} />} />
            <Area
              type="linear"
              dataKey="totalNonDSSubscribedAndOptedInContacts"
              stroke="#2F95E9"
              fillOpacity={1}
              fill="url(#secondaryGradient)"
              dot={secondaryDot}
              activeDot={secondaryActiveDot}
              stackId="1"
            />
            <Area
              type="linear"
              dataKey="totalDSSubscribedAndOptedInContacts"
              stroke={PRIMARY_BASE_COLOR}
              fillOpacity={1}
              fill="url(#primaryGradient)"
              dot={primaryDot}
              activeDot={primaryActiveDot}
              stackId="1"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

SubscribedContactsWidget.propTypes = {
  data: PropTypes.array,
};

export default SubscribedContactsWidget;
